'use client';

import { useEffect } from 'react';

function applyAttributesToDomScript(el: HTMLScriptElement, attributes: Record<string, string>) {
  for (const [name, value] of Object.entries(attributes)) {
    const lower = name.toLowerCase();
    if (lower === 'id') continue;
    if (value === '') {
      if (lower === 'async') {
        el.async = true;
      } else if (lower === 'defer') {
        el.defer = true;
      } else {
        el.setAttribute(name, '');
      }
    } else {
      el.setAttribute(name, value);
    }
  }
}

type PostHydrationExternalScriptProps = {
  mountKey: string;
  src: string;
  attributes: Record<string, string>;
};

/**
 * Injects an external script after React hydration. next/script with beforeInteractive runs
 * before hydration; CMPs like Cookiebot inject DOM (e.g. #CybotCookiebotDialog) and cause
 * "Hydration failed" in Next.js App Router. This runs immediately after mount instead.
 */
export const PostHydrationExternalScript = ({
  mountKey,
  src,
  attributes,
}: PostHydrationExternalScriptProps) => {
  const scriptId = (attributes.id?.trim() || mountKey).trim();
  const _attrFingerprint = JSON.stringify(attributes);

  useEffect(() => {
    if (document.getElementById(scriptId)) return;

    const el = document.createElement('script');
    el.id = scriptId;
    el.src = src;
    applyAttributesToDomScript(el, attributes);
    document.head.appendChild(el);
    // attrFingerprint captures attribute changes; omit `attributes` ref identity from deps
  }, [src, scriptId, attributes]); // eslint-disable-line react-hooks/exhaustive-deps -- attributes serialized in attrFingerprint

  return null;
};

type PostHydrationInlineScriptProps = {
  mountKey: string;
  code: string;
};

export const PostHydrationInlineScript = ({ mountKey, code }: PostHydrationInlineScriptProps) => {
  useEffect(() => {
    if (document.getElementById(mountKey)) return;

    const el = document.createElement('script');
    el.id = mountKey;
    el.textContent = code;
    document.head.appendChild(el);
  }, [mountKey, code]);

  return null;
};
